import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { Alert, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import { useDealInformationTabCount } from '../../DealInformationTabCounts';

import AddCampaignScheduleDialog from './AddCampaignScheduleDialog';
import CampaignScheduleBrandRowsTable from './CampaignScheduleBrandRowsTable';
import CampaignScheduleHistoryCard from './CampaignScheduleHistoryCard';
import ImportedSalesforceDatesSection from './ImportedSalesforceDatesSection';
import ScheduleDoctor from './ScheduleDoctor';
import type { CampaignSchedulesTabProps } from './types';

export default function CampaignSchedulesTab({
  brandSchedules,
  error,
  importCandidates,
  isLoading,
  offerId,
  onImportFromSalesforce,
}: CampaignSchedulesTabProps) {
  const [isAddScheduleOpen, setIsAddScheduleOpen] = useState(false);
  const parentRows = importCandidates?.parentRows ?? [];
  useDealInformationTabCount('campaign-schedules', isLoading || error ? undefined : parentRows.length);

  // `warnings` = rule-based import restrictions (e.g. LuxPlus+ ineligible market), expected noise.
  // `dependencyWarnings` = genuine schedule problems (e.g. missing a purchase schedule) to act on.
  const scheduleDoctorRules = importCandidates?.warnings ?? [];
  const scheduleDoctorGenuineIssues = importCandidates?.dependencyWarnings ?? [];

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading campaign schedules.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Unable to load campaign schedules.</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Stack
        alignItems={{ xs: 'flex-start', md: 'center' }}
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Typography fontWeight={700} variant="h2">
          Campaign Schedules
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
        >
          <Button disabled={!offerId} onClick={() => setIsAddScheduleOpen(true)} startIcon={<AddIcon />} variant="text">
            Add Schedule
          </Button>
          <Button
            disabled={!offerId}
            onClick={onImportFromSalesforce}
            startIcon={<CloudDownloadIcon />}
            variant="contained"
          >
            Import from Salesforce
          </Button>
        </Stack>
      </Stack>

      {offerId && (
        <AddCampaignScheduleDialog
          brandSchedules={brandSchedules}
          offerId={offerId}
          open={isAddScheduleOpen}
          onClose={() => setIsAddScheduleOpen(false)}
        />
      )}

      <ScheduleDoctor genuineIssues={scheduleDoctorGenuineIssues} rules={scheduleDoctorRules} />

      <ImportedSalesforceDatesSection brandSchedules={brandSchedules} offerId={offerId} parentRows={parentRows} />

      <Paper sx={{ borderRadius: 3 }} variant="outlined">
        <Typography fontWeight={700} sx={{ px: 3, pt: 3, pb: 1.5 }} variant="h3">
          Site Schedules
        </Typography>
        <CampaignScheduleBrandRowsTable
          emptyMessage="No schedules yet for this offer."
          offerId={offerId}
          schedules={brandSchedules}
        />
      </Paper>

      <CampaignScheduleHistoryCard offerId={offerId} />
    </Stack>
  );
}
